import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'

import { prepareAndSignPermitAction } from '../../swap/atoms/reatom-permit'

import {
  createLimitOrderRequestAction,
  prepareCreateLimitOrderInstructionsRequestAction,
  prepareNonceInstructionsRequestAction,
} from './limit-order-requests'

import type {
  CreateLimitOrderBaseRequest,
  CreateLimitOrderInstructionsRequest,
  CreateLimitOrderRequest,
} from './limit-order-requests'
import type {
  TradeLimitOrderCreateData,
  TradeLimitOrderPrepareInstructionsCreatePayload,
} from '@blade/trade-api-client'
import type { SignTransactionPayloadDto } from '@root/features/secure-storage/secure-storage-service'

type NonceParam = NonNullable<
  TradeLimitOrderPrepareInstructionsCreatePayload['nonce']
>
const prepareNonceAction = action(
  async (
    ctx,
    req: CreateLimitOrderBaseRequest,
  ): Promise<Result.Result<NonceParam, Error.UnknownError>> => {
    // For typecheck only
    if (req.network !== Network.Solana) {
      return Result.fail(Error.UnknownError.fromUnknown('Invalid network'))
    }

    const nonceInstructionsResult = await ctx.schedule(() =>
      prepareNonceInstructionsRequestAction(ctx, req),
    )

    if (Result.isFailure(nonceInstructionsResult)) {
      return Result.fail(nonceInstructionsResult.failure)
    }

    const nonceInstructions = nonceInstructionsResult.success

    if ('nonce' in nonceInstructions) {
      return Result.succeed(nonceInstructions)
    }

    const { secureStorageService } = ctx.get(dependenciesAtom)
    const signedNonceInstructionsResult = await ctx.schedule(() =>
      secureStorageService.signTransaction({
        tx: nonceInstructions.serializedTransaction,
        network: req.network,
        operation: 'swap',
        password: undefined,
        walletId: req.walletId,
      }),
    )
    if (Result.isFailure(signedNonceInstructionsResult)) {
      return Result.fail(
        Error.UnknownError.fromUnknown(signedNonceInstructionsResult.failure),
      )
    }

    return Result.succeed({
      nonceAccount: nonceInstructions.nonceAccount,
      signedTx: signedNonceInstructionsResult.success.txSigned,
    })
  },
  'prepareNonceAction',
)

export const createLimitOrderAction = action(
  async (
    ctx,
    req: CreateLimitOrderBaseRequest,
  ): Promise<Result.Result<TradeLimitOrderCreateData, Error.UnknownError>> => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const processSolana = async (): Promise<
      Result.Result<TradeLimitOrderCreateData, Error.UnknownError>
    > => {
      // For typecheck only
      if (req.network !== Network.Solana) {
        return Result.fail(Error.UnknownError.fromUnknown('Invalid network'))
      }

      const nonceResult = await ctx.schedule(() => prepareNonceAction(ctx, req))

      if (Result.isFailure(nonceResult)) {
        return Result.fail(nonceResult.failure)
      }

      const instructionsRequest: CreateLimitOrderInstructionsRequest = {
        ...req,
        network: req.network,
        nonce: nonceResult.success,
      }

      const instructionsResult = await ctx.schedule(() =>
        prepareCreateLimitOrderInstructionsRequestAction(
          ctx,
          instructionsRequest,
        ),
      )

      if (Result.isFailure(instructionsResult)) {
        return Result.fail(instructionsResult.failure)
      }

      const instructions = instructionsResult.success
      if (!('nonceAccount' in instructions)) {
        return Result.fail(Error.UnknownError.fromUnknown('Invalid response'))
      }

      const signedCreateInstructionsResult = await ctx.schedule(() =>
        secureStorageService.signTransaction({
          tx: instructions.serializedTransaction,
          network: req.network,
          operation: 'swap',
          password: undefined,
          walletId: req.walletId,
        }),
      )

      if (Result.isFailure(signedCreateInstructionsResult)) {
        return Result.fail(
          Error.UnknownError.fromUnknown(
            signedCreateInstructionsResult.failure,
          ),
        )
      }

      const createRequest: CreateLimitOrderRequest & {
        network: typeof Network.Solana
      } = {
        ...req,
        network: Network.Solana,
        nonce: nonceResult.success,
        signedLimitOrderTxSvm: signedCreateInstructionsResult.success.txSigned,
      }

      const result = await ctx.schedule(() =>
        createLimitOrderRequestAction(ctx, createRequest),
      )

      return result
    }

    const processEvm = async (): Promise<
      Result.Result<TradeLimitOrderCreateData, Error.UnknownError>
    > => {
      // For typecheck only
      if (
        req.network !== Network.Ethereum &&
        req.network !== Network.Base &&
        req.network !== Network.Bsc
      ) {
        return Result.fail(Error.UnknownError.fromUnknown('Invalid network'))
      }

      const signedPermitResult = await ctx.schedule(() =>
        prepareAndSignPermitAction(ctx, {
          amount: req.amount.toString(),
          tokenAddress: req.currencyAddress,
          walletId: req.walletId,
          walletAddress: req.walletAddress,
          network: Network.toDto(req.network),
          provider: req.provider.id,
          deadline: req.deadline,
        }),
      )

      if (Result.isFailure(signedPermitResult)) {
        return Result.fail(signedPermitResult.failure)
      }

      const instructionsRequest: CreateLimitOrderInstructionsRequest = {
        ...req,
        network: req.network,
        permit: {
          permitSingle: signedPermitResult.success.permitSingle,
          signedPermit: signedPermitResult.success.signedPermit,
        },
      }

      const instructionsResult = await ctx.schedule(() =>
        prepareCreateLimitOrderInstructionsRequestAction(
          ctx,
          instructionsRequest,
        ),
      )

      if (Result.isFailure(instructionsResult)) {
        return Result.fail(instructionsResult.failure)
      }

      const instructions = instructionsResult.success
      if (!('signatures' in instructions)) {
        return Result.fail(Error.UnknownError.fromUnknown('Invalid response'))
      }

      const signedInstructionsResult = await ctx.schedule(() =>
        secureStorageService.signTransactions(
          instructions.signatures.map(
            (signature): SignTransactionPayloadDto => ({
              tx: signature,
              network: req.network,
              operation: 'swap',
              password: undefined,
              walletId: req.walletId,
            }),
          ),
        ),
      )

      if (Result.isFailure(signedInstructionsResult)) {
        return Result.fail(
          Error.UnknownError.fromUnknown(signedInstructionsResult.failure),
        )
      }

      const createRequest: CreateLimitOrderRequest & {
        network: Network.EvmNetwork
      } = {
        ...req,
        network: req.network,
        permit: signedPermitResult.success,
        signedLimitOrderTxsEvm: {
          minNonce: instructions.minNonce,
          maxNonce: instructions.maxNonce,
          signatures: signedInstructionsResult.success.map(
            (result) => result.txSigned,
          ),
        },
      }

      const result = await ctx.schedule(() =>
        createLimitOrderRequestAction(ctx, createRequest),
      )

      return result
    }

    if (req.network === Network.Solana) {
      return processSolana()
    }

    return processEvm()
  },
  'createLimitOrderAction',
)

export const reatomCreateLimitOrder = () => {
  const {
    executeAction: createOrderAction,
    requestStateAtom: createStateAtom,
    resetStateAction: resetCreateOrderAction,
  } = reatomRequest({
    execAction: createLimitOrderAction,
    name: 'reatomCreateLimitOrder',
  })

  return {
    createOrderAction,
    createStateAtom,
    resetCreateOrderAction,
  }
}
